import { FC, ReactNode, RefObject } from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineX } from "react-icons/hi";
import { FilterCountBadge } from "./FilterCountBadge";

export interface MultiSelectFilterOption {
  key: string;
  label: ReactNode;
  selected: boolean;
  /** Optional native title tooltip (used for long company names). */
  title?: string;
}

export interface MultiSelectFilterDropdownProps {
  containerRef: RefObject<HTMLDivElement>;
  isOpen: boolean;
  onToggle: (e: React.MouseEvent) => void;
  icon: ReactNode;
  label: string;
  options: MultiSelectFilterOption[];
  onOptionClick: (key: string, e: React.MouseEvent) => void;
  headerTitle: string;
  /** Number of active selections; drives the badge and in-header clear button. */
  selectedCount: number;
  /** When provided, an in-header "Clear" button shows while selectedCount > 0. */
  onClear?: () => void;
  /** Extra trigger classes (e.g. "min-w-[115px]"). */
  triggerExtraClassName?: string;
  /** Full panel classes; defaults to the shared shell. */
  panelClassName?: string;
  panelStyle?: React.CSSProperties;
  /** Extra option button classes (e.g. "focus:outline-none focus:ring-0"). */
  optionButtonExtraClassName?: string;
  /** Extra checkbox classes (e.g. "shrink-0"). */
  inputExtraClassName?: string;
  /** Option label span classes; defaults to "dark:text-white". */
  spanClassName?: string;
}

const triggerBaseClassName =
  "flex items-center justify-center md:justify-between bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500";

const defaultPanelClassName =
  "absolute z-10 mt-1 bg-white rounded-lg shadow-lg dark:bg-gray-700 dark:border-gray-600";

/**
 * Checkbox (multi-select) filter dropdown shell. Reproduces the status/company
 * filter dropdowns repeated across the orders and invoices pages.
 */
export const MultiSelectFilterDropdown: FC<MultiSelectFilterDropdownProps> = ({
  containerRef,
  isOpen,
  onToggle,
  icon,
  label,
  options,
  onOptionClick,
  headerTitle,
  selectedCount,
  onClear,
  triggerExtraClassName = "",
  panelClassName = defaultPanelClassName,
  panelStyle,
  optionButtonExtraClassName = "",
  inputExtraClassName = "",
  spanClassName = "dark:text-white",
}) => {
  const { t } = useTranslation(["common"]);
  const isActive = selectedCount > 0;

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={onToggle}
        className={`${triggerBaseClassName} ${triggerExtraClassName} ${isActive ? "border-brand dark:border-brand" : ""}`}
      >
        <div className="flex items-center">
          {icon}
          <span className="hidden md:inline ml-2">{label}</span>
          {isActive && <FilterCountBadge count={selectedCount} />}
        </div>
      </button>

      {isOpen && (
        <div
          className={panelClassName}
          style={panelStyle}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-3 border-b border-border">
            <div className="flex justify-between items-center">
              <span className="font-medium dark:text-white">{headerTitle}</span>
              {onClear && isActive && (
                <button
                  className="text-sm text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClear();
                  }}
                >
                  <HiOutlineX className="mr-1 h-4 w-4 inline" />
                  {t("common:Clear")}
                </button>
              )}
            </div>
          </div>
          <ul className="py-2 max-h-60 overflow-y-auto">
            {options.map((option) => (
              <li key={option.key}>
                <button
                  onClick={(e) => onOptionClick(option.key, e)}
                  className={`flex items-center w-full px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 ${optionButtonExtraClassName} ${option.selected ? "bg-brand-muted dark:bg-lgb-blue-400/30" : ""}`}
                  title={option.title}
                >
                  <input
                    type="checkbox"
                    checked={option.selected}
                    onChange={() => {}}
                    className={`mr-2 w-4 h-4 text-brand bg-gray-100 border-gray-300 rounded focus:ring-brand dark:focus:ring-brand dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 ${inputExtraClassName}`}
                  />
                  <span className={spanClassName}>{option.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
